import type { AgentSuggestionOp } from '@onefootprint/request-types/dashboard';
import type { Bundle, DecisionNode, RulesNode, Task } from './types';
import { isDecisionNode, isRulesNode, isTaskNode } from './types';

export type OutputOption = {
  value: string;
  label: string;
  description?: string;
  /** The producing step's 1-based place in the plan: the number its page and the nav show. */
  position?: number;
};

export const judgmentOptions = (bundle: Bundle): OutputOption[] =>
  bundle.nodes.flatMap(node => {
    if (isTaskNode(node)) {
      return node.fields
        .filter(field => field.name.trim().length > 0)
        .map(field => ({ value: field.name, label: field.name, description: node.name || undefined }));
    }
    if (isRulesNode(node) && node.key.trim().length > 0) {
      return [{ value: node.key, label: node.key, description: node.name || undefined }];
    }
    if (isDecisionNode(node)) {
      return node.questions
        .filter(question => question.key.trim().length > 0)
        .map(question => ({ value: question.key, label: question.key, description: node.name || undefined }));
    }
    return [];
  });

// An investigation shows the verdict as one short value, so only outputs with a fixed set of values
// qualify. Rules and decision steps lead: that's usually where a verdict comes from.
export const verdictOptions = (bundle: Bundle): OutputOption[] => {
  const step = (node: Task | RulesNode | DecisionNode) => ({
    description: node.name || undefined,
    position: bundle.nodes.indexOf(node) + 1,
  });
  const decisions = bundle.nodes.filter(isRulesNode).filter(node => node.key.trim().length > 0);
  const questions = bundle.nodes
    .filter(isDecisionNode)
    .flatMap(node =>
      node.questions
        .filter(question => question.kind !== 'score' && question.key.trim().length > 0)
        .map(question => ({ value: question.key, label: question.key, ...step(node) })),
    );
  const fields = bundle.nodes
    .filter(isTaskNode)
    .flatMap(node =>
      node.fields
        .filter(field => field.name.trim().length > 0 && (field.type === 'enum' || field.type === 'boolean'))
        .map(field => ({ value: field.name, label: field.name, ...step(node) })),
    );
  return [...decisions.map(node => ({ value: node.key, label: node.key, ...step(node) })), ...questions, ...fields];
};

export const artifactOptions = (bundle: Bundle): OutputOption[] =>
  bundle.nodes.flatMap((node, index) =>
    isTaskNode(node)
      ? node.artifacts.map(artifact => ({
          value: artifact.id,
          label: artifact.name || artifact.id,
          description: node.name || undefined,
          position: index + 1,
        }))
      : [],
  );

export const reportsRemovedBy = (operation: AgentSuggestionOp, bundle: Bundle): string[] => {
  if (operation.kind === 'remove_task_artifact') return [operation.key];
  if (operation.kind !== 'remove_node') return [];
  const node = bundle.nodes.find(candidate => candidate.id === operation.nodeId);
  return node && isTaskNode(node) ? node.artifacts.map(artifact => artifact.id) : [];
};
